<template>
  <TableList
    v-rect-loading="loading"
    defaultExpandAll
    class="job-posting-channel-account-management-list"
    row-key="id"
    :createBtnLabel="$t(`${i18nPrefix}.btn.addAccount`)"
    :title="$t(`${i18nPrefix}.title.jobPostingChannelAccountManagement`)"
    :listData="dataList"
    :tableColumns="tableColumns"
    :actionBtn="actionBtn"
    @create="handleCreate"
  >
    <template #filter>
      <el-input
        v-model="keywords"
        clearable
        class="keywords-filter"
        :placeholder="$t(`${i18nPrefix}.placeholder.username`)"
        @input="handleKeywordsChange"
      ></el-input>
    </template>
    <template #status="{data, row}">
      <el-switch v-if="row.isInfo" :value="!data" @change="handleChangeStatus($event, row)"/>
    </template>
    <template #page-bottom>
      <DialogWrapper
        :show.sync="dialog.show"
        :loading="dialog.loading"
        :title="dialog.title"
        @save="confirmDelete"
      >
      </DialogWrapper>
    </template>
  </TableList>
</template>

<script>
import { getI18nPropertyValue } from '@/i18n'
import _ from 'lodash'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { CHANNEL_ID, DEFAULT_CHANNEL_ID } from '@/constant/job'
import { mapActions, mapGetters } from 'vuex'

export default {
  components: {
    TableList,
    DialogWrapper
  },
  props: {},
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.jobPostingChannelAccount',
      dataList: [],
      keywords: null,
      dialog: {
        show: false,
        loading: false,
        title: null,
        deleteId: null
      }
    }
  },
  computed: {
    ...mapGetters('account', ['getCurrentAccount']),
    currentAccount () {
      return this.getCurrentAccount
    },
    hasJob51Channel () {
      return _.find(this.dataList, { channelId: CHANNEL_ID.JOB51 })
    },
    tableColumns () {
      const list = [
        {
          label: this.$t(`${this.i18nPrefix}.label.channelName`),
          dataKey: 'channelName',
          formatter: (data, rowData) => {
            return rowData.isInfo ? null : data
          }
        },
        {
          hidden: !this.hasJob51Channel,
          label: this.$t(`${this.i18nPrefix}.label.job51MemberName`),
          dataKey: 'job_51_member_name'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.username`),
          dataKey: 'username',
          width: '150px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'is_disabled',
          slotName: 'status',
          width: '50px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.authorizedUser`),
          dataKey: 'authorizedUser',
          width: '150px'
        }
      ]
      return _.filter(list, item => !item.hidden)
    },
    actionBtn () {
      return {
        edit: {
          show: (row) => row.isInfo,
          onclick: this.handleEdit
        },
        delete: {
          show: (row) => row.isInfo,
          onclick: this.handleDelete
        }
      }
    }
  },
  created () {
    this.initData()
  },
  mounted () {

  },
  methods: {
    ...mapActions('jobPostingChannelAccount', ['getJobPostingChannelAccountList', 'updateJobPostingChannelAccount', 'deleteJobPostingChannelAccount']),
    ...mapActions('company', ['getCompanyJobPostingChannelList']),
    async initData () {
      this.loading = true
      try {
        await this.loadCompanyJobPostingChannelList()
        await this.searchJobPostingChannelAccountList()
      } finally {
        this.loading = false
      }
    },
    async loadCompanyJobPostingChannelList () {
      const resp = await this.getCompanyJobPostingChannelList({ company_id: this.currentAccount.company_id })
      if (resp) {
        const vm = this
        this.dataList = _.chain(resp.post_channels)
          .filter(channel => channel.id !== DEFAULT_CHANNEL_ID && channel.manual_job_posting_can_use)
          .map(channel => {
            return {
              id: `group-${channel.id}`,
              get channelName () {
                if (channel.id === CHANNEL_ID.BOSS) {
                  return vm.$t(`${vm.i18nPrefix}.label.boss`)
                } else if (channel.id === CHANNEL_ID.LIE_TOU) {
                  return vm.$t(`${vm.i18nPrefix}.label.lietou`)
                }
                return getI18nPropertyValue(channel, 'cn_name', 'name')
              },
              channelId: channel.id,
              children: []
            }
          })
          .value()
      }
    },
    async searchJobPostingChannelAccountList () {
      this.parseQuery()
      if (!this.loading) {
        this.$eventBus.$emit('showLoading', true)
      }
      try {
        const resp = await this.getJobPostingChannelAccountList({ keywords: this.keywords })
        if (resp) {
          const vm = this
          _.forEach(this.dataList, (group) => {
            group.children = []
          })
          _.chain(resp)
            .groupBy('channel_id')
            .forEach((channelList, channelId) => {
              const channelData = _.find(this.dataList, { channelId: Number(channelId) })
              channelData.children = _.chain(channelList)
                .orderBy(['is_disabled'], ['asc'])
                .map(channel => {
                  return {
                    ...channel,
                    channelName: channelData.channelName,
                    get authorizedUser () {
                      return channel.is_public
                        ? vm.$t(`${vm.i18nPrefix}.label.all`)
                        : _.chain(channel.bind_accounts)
                          .map(account => account.email)
                          .join(', ')
                          .value()
                    },
                    isInfo: true
                  }
                })
                .value()
            })
            .value()
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(this.formatSearchData, 1000)
      }
      this.keywordsSearchDebounce()
    },
    async handleChangeStatus (status, row) {
      this.$eventBus.$emit('showLoading', true)
      try {
        const data = {
          is_disabled: !status
        }
        const resp = await this.updateJobPostingChannelAccount({ id: row.id, data })
        if (resp) {
          row.is_disabled = !status
          this.$message.success(this.$t('common.message.optionSuccess'))
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async confirmDelete () {
      this.dialog.loading = true
      try {
        const resp = await this.deleteJobPostingChannelAccount(this.dialog.deleteId)
        if (resp) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.deleteSuccess`))
          this.dialog.show = false
          this.searchJobPostingChannelAccountList()
        }
      } finally {
        this.dialog.loading = false
      }
    },
    handleCreate () {
      this.$router.push({
        path: '/v-admin/job-posting-channel-account/management/add'
      })
    },
    handleEdit (row) {
      this.$router.push({
        path: `/v-admin/job-posting-channel-account/management/edit/${row.id}`
      })
    },
    handleDelete (row) {
      this.dialog.title = this.$t(`${this.i18nPrefix}.message.deleteDialogTitle`, { channel: row.channelName, username: row.username })
      this.dialog.deleteId = row.id
      this.dialog.show = true
    },
    formatSearchData () {
      if (this.$route.query.keywords === this.keywords) {
        return
      }
      this.$router.push({
        query: {
          ...this.$route.query,
          keywords: this.keywords
        }
      })
    },
    parseQuery () {
      this.keywords = this.$route.query.keywords
    }
  },
  watch: {
    '$route' () {
      this.searchJobPostingChannelAccountList()
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
